/**
 * CR429: Minimal requirements registry for canonical section IDs.
 *
 * Each entry describes the deal-JSON fields a section needs to render.
 * - anyOf: at least ONE group of fields must all be present → section can render.
 * - allOf: ALL listed fields must be present.
 * - note: human-friendly explanation.
 */

export interface SectionRequirement {
  /** At least one group of fields must all be non-null for the section to render */
  anyOf?: string[][];
  /** All listed fields must be non-null */
  allOf?: string[];
  /** Human-friendly note */
  note?: string;
}

export const SECTION_REQUIREMENTS: Record<string, SectionRequirement> = {
  value_box: {
    anyOf: [
      ["value.computed_value_cad"],
      ["value.effective_percent_back", "value.qualifying_spend_cad"],
      ["computed_value_cad"],
    ],
    note: "Value box needs a computed value or the inputs to derive it.",
  },
  spend_box: {
    anyOf: [
      ["value.qualifying_spend_cad"],
      ["qualifying_spend_cad"],
    ],
    note: "Spend box needs qualifying spend.",
  },
  reward_box: {
    anyOf: [
      ["deal_type"],
      ["reward_type"],
      ["benefit_type"],
    ],
    note: "Reward box needs deal type / reward info.",
  },
  cta_button: {
    anyOf: [
      ["claim_url"],
      ["detail_link"],
      ["sources.0.detail_link"],
    ],
    note: "CTA needs a URL to send users to.",
  },
  eligibility_box: {
    anyOf: [
      ["eligibility_requirements"],
      ["value.eligibility_requirements"],
    ],
    note: "Eligibility box needs eligibility requirements data.",
  },
};

// ================================================================
// Field access helper — supports dot paths + array indices
// ================================================================

/**
 * Resolve a dot-path like "value.computed_value_cad" or "sources.0.detail_link"
 * against an arbitrary object. Returns the value or undefined.
 */
export function getNestedField(obj: Record<string, unknown> | null | undefined, path: string): unknown {
  if (!obj) return undefined;
  const parts = path.split(".");
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Check if a field at the given dot-path is "present" (non-null, non-undefined).
 */
export function hasField(obj: Record<string, unknown> | null | undefined, path: string): boolean {
  const val = getNestedField(obj, path);
  return val != null;
}
